import React, { useRef, useEffect, useState, useCallback } from 'react';
import * as d3 from 'd3-force';
import { alpha } from '@mentimeter/ragnar-colors';
import { LOW_OPACITY } from '../bars/constants';

const ANIMATION_DURATION = 500; // Animation duration in ms

interface Circle {
  id: string;
  color: string;
  segmentId?: string;
}

interface AnimatedCircle extends Circle {
  animatedRadius: number;
  animationStartTime: number | undefined;
  x: number;
  y: number;
  // velocity is set by the simulation
  vx?: number;
  vy?: number;
}

interface DotsClusterProps {
  dots: Circle[];
  radius: number;
  width: number;
  height: number;
  fadedSegments?: Set<Circle['segmentId']> | undefined;
  disableAnimations: boolean;
  canvasOpacity?: number;
}

export const DotsCluster = ({
  dots,
  radius,
  width,
  height,
  fadedSegments,
  disableAnimations,
  canvasOpacity,
}: DotsClusterProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [circles, setCircles] = useState<AnimatedCircle[]>([]);
  const circlesRef = useRef<AnimatedCircle[]>(circles);
  const simulationRef = useRef<d3.Simulation<AnimatedCircle, undefined> | null>(
    null,
  );
  const animationRef = useRef<number>();

  // Helper function to calculate animated radius without mutation
  const getAnimatedRadius = useCallback(
    (circle: AnimatedCircle): number => {
      if (disableAnimations) {
        return radius;
      }

      if (!circle.animationStartTime || circle.animatedRadius >= radius) {
        return circle.animatedRadius;
      }

      const elapsed = Date.now() - circle.animationStartTime;
      const progress = Math.min(elapsed / ANIMATION_DURATION, 1);
      return (
        circle.animatedRadius + progress * (radius - circle.animatedRadius)
      );
    },
    [radius, disableAnimations],
  );

  // Create a new simulation
  useEffect(() => {
    const simulation = d3
      .forceSimulation(circles)
      .alphaTarget(0.1) // little movement at rest
      .velocityDecay(0.1) // low friction
      .force('x', d3.forceX(width / 2).strength(0.005)) // Center force x
      .force('y', d3.forceY(height / 2).strength(0.005)) // Center force y
      .force(
        'collide', // Keep dots from overlapping
        d3
          .forceCollide<AnimatedCircle>()
          .radius((d) => radius + 1)
          .iterations(3),
      );

    if (disableAnimations) {
      // Run simulation for many ticks to reach equilibrium immediately
      simulation.tick(150);
      simulation.stop();
    }

    simulationRef.current = simulation;

    return () => {
      simulation.stop();
    };
  }, [width, height, radius, circles, disableAnimations]);

  // Update the simulation when the circles change
  useEffect(() => {
    circlesRef.current = circles; // Keep ref in sync with state
    if (simulationRef.current) {
      simulationRef.current.nodes([...circles]);
      simulationRef.current.restart();
    }
  }, [circles]);

  // React to dots changes from parent component
  useEffect(() => {
    setCircles((prevCircles) => {
      // Remove dots that are no longer present in the dots array
      const remainingCircles = prevCircles
        .filter((c) => dots.some((d) => d.id === c.id))
        .map((c) => ({
          ...c,
          color: dots.find((d) => d.id === c.id)?.color || c.color,
        }));

      const existingIds = new Set(remainingCircles.map((c) => c.id));

      const newCircles = dots
        .filter((dot) => !existingIds.has(dot.id))
        .map((dot) => ({
          ...dot,
          // Randomize start position
          x: Math.random() * width,
          y: Math.random() * height,
          animationStartTime: Date.now(),
          animatedRadius: 0,
        }));

      return [...remainingCircles, ...newCircles];
    });
  }, [dots, radius, width, height]);

  // Animation loop for canvas rendering
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const animate = () => {
      // Clear canvas
      ctx.clearRect(0, 0, width, height);

      // Draw circles
      circlesRef.current.forEach((circle) => {
        const currentRadius = getAnimatedRadius(circle);
        const isFadedOut = fadedSegments?.has(circle.segmentId);

        if (currentRadius > 0) {
          ctx.beginPath();
          ctx.arc(circle.x, circle.y, currentRadius, 0, 2 * Math.PI);
          if (isFadedOut) {
            ctx.fillStyle = alpha(circle.color, LOW_OPACITY);
          } else {
            ctx.fillStyle = circle.color;
          }
          ctx.fill();
        }
      });

      animationRef.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [width, height, radius, getAnimatedRadius, fadedSegments]);

  return (
    <canvas
      style={{
        opacity: canvasOpacity,
        transition: disableAnimations ? 'none' : 'opacity 0.3s ease-in-out',
      }}
      ref={canvasRef}
      width={width}
      height={height}
    />
  );
};
